import React, { useState } from "react";
import { Lock } from "lucide-react";
import { BeaconMedia } from "../pages/BeaconMedia";
import { ArtLightbox } from "./ArtLightbox";
import { useArt, useCaption, artLine, statusLabel } from "../lib/artwork";
import { ArtStory, InterestedButton } from "./ArtStory";

export const SoldMark = ({ art }) => (art?.status === "sold" && art.sold_mark
  ? <span className="absolute top-2 right-2 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm px-2 py-0.5 font-body text-[10px] tracking-[0.2em] uppercase text-white" data-testid="art-sold-mark"><span className="w-1.5 h-1.5 rounded-full bg-[#C0392B]" />Sold</span>
  : null);

const Caption = ({ it, art, accent, full, cap, encKey, noTitle }) => (
  <div className={`font-body ${full ? "mt-3 space-y-1" : "mt-1.5"}`}>
    {it.title && !noTitle && <div className={`text-white ${full ? "text-base" : "text-xs truncate"}`}>{it.title}</div>}
    {full && art && artLine(art) && <div className="text-sm text-zinc-400">{artLine(art)}</div>}
    {full && cap && <p className="text-sm text-zinc-400 whitespace-pre-wrap" data-testid="art-caption">{cap}</p>}
    {art && (full || art.status !== "available") && (
      <div className="flex flex-wrap gap-3 text-[11px]">
        <span style={{ color: accent }}>{statusLabel(art.status)}</span>
        {full && art.show_price && art.price && <span className="text-zinc-300" data-testid="art-price">{art.price}</span>}
      </div>
    )}
    {full && art && <ArtStory it={it} art={art} encKey={encKey} accent={accent} />}
    {full && art && <div className="pt-2"><InterestedButton it={it} art={art} accent={accent} /></div>}
  </div>
);

const Tile = ({ it, layout, encKey, accent, onOpen }) => {
  const art = useArt(it, encKey);
  const cap = useCaption(it, encKey);
  const box = layout === "grid" ? "aspect-square" : "";
  if (it.locked) return (
    <div className={`${layout === "masonry" ? "mb-3 break-inside-avoid aspect-[4/5]" : layout === "single" ? "aspect-[4/3]" : box} bg-[#121212] border border-white/10 flex flex-col items-center justify-center gap-2 text-zinc-500`} data-testid={`art-tile-locked-${it.id}`}>
      <Lock size={18} /><span className="font-body text-xs px-3 text-center line-clamp-2">{it.title || "Members only"}</span>
    </div>
  );
  return (
    <div className={layout === "masonry" ? "mb-3 break-inside-avoid" : ""} data-testid={`art-tile-${it.id}`}>
      <button type="button" onClick={onOpen} className={`relative block w-full overflow-hidden bg-white/5 group focus:outline-none focus-visible:ring-1 focus-visible:ring-white/40 ${box}`} aria-label={`Open ${it.title || "artwork"}`}>
        <BeaconMedia path={it.media} type="image" encKey={it.enc ? encKey : null} iv={it.media_iv}
          className={`w-full ${layout === "grid" ? "h-full object-cover" : "h-auto"} transition-transform duration-500 group-hover:scale-[1.02]`} />
        <SoldMark art={art} />
      </button>
      <Caption it={it} art={art} accent={accent} full={layout === "single"} cap={cap} encKey={encKey} />
    </div>
  );
};

const WRAP = { grid: "grid grid-cols-2 sm:grid-cols-3 gap-3", masonry: "columns-2 sm:columns-3 gap-3", single: "space-y-10" };

// Collection gallery: artworks in the chosen layout, other lessons listed below as usual.
export const ArtGallery = ({ section, encKey, accent, renderItem }) => {
  const [open, setOpen] = useState(null);
  const arts = section.items.filter((it) => it.type === "artwork");
  const viewable = arts.filter((it) => !it.locked && it.media);
  const others = section.items.filter((it) => it.type !== "artwork");
  return (
    <div className="pt-3 border-t border-white/5" data-testid={`art-gallery-${section.id}`} data-layout={section.layout}>
      <div className={WRAP[section.layout] || WRAP.grid}>
        {arts.map((it) => <Tile key={it.id} it={it} layout={section.layout} encKey={encKey} accent={accent} onOpen={() => setOpen(viewable.findIndex((v) => v.id === it.id))} />)}
      </div>
      {others.length > 0 && <div className="mt-4">{others.map((it) => renderItem(it, section))}</div>}
      {open !== null && open >= 0 && <ArtLightbox items={viewable} index={open} encKey={encKey} accent={accent} onClose={() => setOpen(null)} />}
    </div>
  );
};

// A single artwork inside a normal list section.
export const ArtworkInline = ({ it, encKey, accent }) => {
  const [open, setOpen] = useState(false);
  const art = useArt(it, encKey);
  return (
    <div data-testid={`art-inline-${it.id}`}>
      <button type="button" onClick={() => setOpen(true)} className="relative inline-block max-w-full overflow-hidden focus:outline-none focus-visible:ring-1 focus-visible:ring-white/40" aria-label="Open artwork">
        <BeaconMedia path={it.media} type="image" encKey={it.enc ? encKey : null} iv={it.media_iv} className="max-h-96 rounded" />
        <SoldMark art={art} />
      </button>
      {art && <Caption it={it} art={art} accent={accent} full encKey={encKey} noTitle />}
      {open && <ArtLightbox items={[it]} index={0} encKey={encKey} accent={accent} onClose={() => setOpen(false)} />}
    </div>
  );
};
